import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useApiQuery, useApiMutation } from '../hooks/useApi';
import { apiClient } from '../utils/apiClient';
import './Trips.css';

interface GearItem {
  gearId: string;
  name: string;
  category: string;
  weight?: number;
  notes?: string;
}

interface TripGearItem {
  gearId: string;
  name: string;
  category: string;
  packed: boolean;
  packedAt?: string;
}

interface Trip {
  tripId: string;
  name: string;
  startDate: string;
  endDate: string;
  templateId?: string;
  gearItems: TripGearItem[];
  createdAt: string;
  updatedAt: string;
}

interface Template {
  templateId: string;
  name: string;
  gearItems: Array<{
    gearId: string;
    name: string;
    category: string;
  }>;
}

interface TripFormData {
  name: string;
  startDate: string;
  endDate: string;
  templateId: string;
  gearItems: TripGearItem[];
}

interface TripProgress {
  totalItems: number;
  packedItems: number;
  percentage: number;
  categoriesProgress: Array<{
    category: string;
    total: number;
    packed: number;
    percentage: number;
  }>;
}

const CATEGORIES = [
  { value: 'shelter', label: 'Shelter', icon: '🏕️' },
  { value: 'sleep', label: 'Sleep', icon: '🛌' },
  { value: 'cooking', label: 'Cooking', icon: '🍳' },
  { value: 'food', label: 'Food', icon: '🍎' },
  { value: 'water', label: 'Water', icon: '💧' },
  { value: 'clothing', label: 'Clothing', icon: '👕' },
  { value: 'electronics', label: 'Electronics', icon: '🔌' },
  { value: 'safety', label: 'Safety', icon: '🚨' },
  { value: 'first_aid', label: 'First Aid', icon: '⛑️' },
  { value: 'navigation', label: 'Navigation', icon: '🧭' },
  { value: 'hygiene', label: 'Hygiene', icon: '🧼' },
  { value: 'tools', label: 'Tools', icon: '🔧' },
  { value: 'recreation', label: 'Recreation', icon: '🎿' },
  { value: 'other', label: 'Other', icon: '📦' },
];

const Trips: React.FC = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showTripDetail, setShowTripDetail] = useState<Trip | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showCustomizeTrip, setShowCustomizeTrip] = useState<Trip | null>(null);
  const [formData, setFormData] = useState<TripFormData>({
    name: '',
    startDate: '',
    endDate: '',
    templateId: '',
    gearItems: [],
  });
  const [formErrors, setFormErrors] = useState<{ 
    name?: string; 
    startDate?: string; 
    endDate?: string; 
    templateId?: string; 
  }>({});
  const [gearSearchTerm, setGearSearchTerm] = useState('');
  const [selectedGearCategory, setSelectedGearCategory] = useState('');

  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === 'true') {
      setShowCreateForm(true);
    }
  }, [location.search]);

  // API hooks
  const { data: trips, loading, error, refetch } = useApiQuery<Trip[]>(
    () => apiClient.getTrips(),
    []
  );

  const { data: templates, loading: templatesLoading } = useApiQuery<Template[]>(
    () => apiClient.getTemplates(),
    []
  );

  const { data: gearItems, loading: gearLoading } = useApiQuery<GearItem[]>(
    () => apiClient.getGear(),
    []
  );

  const { mutate: createTrip, loading: creating } = useApiMutation<Trip>();
  const { mutate: updateTrip, loading: updating } = useApiMutation<Trip>();
  const { mutate: deleteTrip, loading: deleting } = useApiMutation<void>();
  const { mutate: updateItemStatus, loading: updatingStatus } = useApiMutation<any>();

  // Calculate trip status based on dates
  const getTripStatus = (trip: Trip): 'upcoming' | 'active' | 'completed' => {
    const now = new Date();
    const startDate = new Date(trip.startDate);
    const endDate = new Date(trip.endDate);
    
    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'completed';
    return 'active';
  };

  // Calculate packing progress
  const calculateProgress = (trip: Trip): TripProgress => {
    const totalItems = trip.gearItems.length;
    const packedItems = trip.gearItems.filter(item => item.packed).length;
    const percentage = totalItems > 0 ? Math.round((packedItems / totalItems) * 100) : 0;

    // Calculate progress by category
    const categoriesMap = new Map<string, { total: number; packed: number }>();
    
    trip.gearItems.forEach(item => {
      const current = categoriesMap.get(item.category) || { total: 0, packed: 0 };
      current.total += 1;
      if (item.packed) current.packed += 1;
      categoriesMap.set(item.category, current);
    });

    const categoriesProgress = Array.from(categoriesMap.entries()).map(([category, stats]) => ({
      category,
      total: stats.total,
      packed: stats.packed,
      percentage: Math.round((stats.packed / stats.total) * 100),
    }));

    return {
      totalItems,
      packedItems,
      percentage,
      categoriesProgress,
    };
  };

  // Filter trips based on search and status
  const filteredTrips = useMemo(() => {
    if (!trips) return [];

    return trips.filter(trip => {
      const matchesSearch = trip.name.toLowerCase().includes(searchTerm.toLowerCase());
      const tripStatus = getTripStatus(trip);
      const matchesStatus = !selectedStatus || tripStatus === selectedStatus;
      
      return matchesSearch && matchesStatus;
    });
  }, [trips, searchTerm, selectedStatus]);

  // Filter gear items for customization
  const filteredGearItems = useMemo(() => {
    if (!gearItems || !showCustomizeTrip) return [];

    return gearItems.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(gearSearchTerm.toLowerCase());
      const matchesCategory = !selectedGearCategory || item.category === selectedGearCategory;
      const notAlreadyInTrip = !showCustomizeTrip.gearItems.some(gi => gi.gearId === item.gearId);
      
      return matchesSearch && matchesCategory && notAlreadyInTrip;
    });
  }, [gearItems, gearSearchTerm, selectedGearCategory, showCustomizeTrip]);

  // Form validation
  const validateForm = (): boolean => {
    const errors: { name?: string; startDate?: string; endDate?: string; templateId?: string } = {};

    if (!formData.name.trim()) {
      errors.name = 'Trip name is required';
    }

    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      errors.endDate = 'End date is required';
    } else if (formData.startDate && new Date(formData.endDate) < new Date(formData.startDate)) {
      errors.endDate = 'End date must be after start date';
    }

    if (!formData.templateId) {
      errors.templateId = 'Please select a template';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const tripData = {
      name: formData.name.trim(),
      startDate: formData.startDate,
      endDate: formData.endDate,
      templateId: formData.templateId,
      gearItems: formData.gearItems,
    };

    const success = await createTrip(() => apiClient.createTrip(tripData));

    if (success) {
      handleCloseCreateForm();
      refetch();
    }
  };

  // Handle trip deletion
  const handleDelete = async (tripId: string) => {
    const success = await deleteTrip(() => apiClient.deleteTrip(tripId));
    
    if (success) {
      setShowDeleteConfirm(null);
      refetch();
    }
  };

  // Handle item packing status toggle
  const handleToggleItemStatus = async (tripId: string, itemId: string, packed: boolean) => {
    const success = await updateItemStatus(() => 
      apiClient.updateTripItemStatus(tripId, itemId, packed)
    );
    
    if (success) {
      refetch();
      // Update local state if viewing trip detail
      if (showTripDetail && showTripDetail.tripId === tripId) {
        const updatedTrip = { ...showTripDetail };
        const itemIndex = updatedTrip.gearItems.findIndex(item => item.gearId === itemId);
        if (itemIndex !== -1) {
          updatedTrip.gearItems[itemIndex].packed = packed;
          if (packed) {
            updatedTrip.gearItems[itemIndex].packedAt = new Date().toISOString();
          } else {
            delete updatedTrip.gearItems[itemIndex].packedAt;
          }
          setShowTripDetail(updatedTrip);
        }
      }
    }
  };

  // Form management
  const handleOpenCreateForm = () => {
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      templateId: '',
      gearItems: [],
    });
    setFormErrors({});
    setShowCreateForm(true);
  };

  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      templateId: '',
      gearItems: [],
    });
    setFormErrors({});
  };

  const handleInputChange = (field: keyof TripFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    const template = templates?.find(t => t.templateId === templateId);
    if (template) {
      const tripGearItems: TripGearItem[] = template.gearItems.map(item => ({
        gearId: item.gearId,
        name: item.name,
        category: item.category,
        packed: false,
      }));
      
      setFormData(prev => ({
        ...prev,
        templateId,
        gearItems: tripGearItems,
      }));
    }
  };

  // Trip customization
  const handleOpenCustomizeTrip = (trip: Trip) => {
    setShowCustomizeTrip(trip);
    setGearSearchTerm('');
    setSelectedGearCategory('');
  };

  const handleAddGearToTrip = (gear: GearItem) => {
    if (!showCustomizeTrip) return;

    const newGearItem: TripGearItem = {
      gearId: gear.gearId,
      name: gear.name,
      category: gear.category,
      packed: false,
    };

    const updatedTrip = {
      ...showCustomizeTrip,
      gearItems: [...showCustomizeTrip.gearItems, newGearItem],
    };

    setShowCustomizeTrip(updatedTrip);
  };

  const handleRemoveGearFromTrip = (gearId: string) => {
    if (!showCustomizeTrip) return;

    const updatedTrip = {
      ...showCustomizeTrip,
      gearItems: showCustomizeTrip.gearItems.filter(item => item.gearId !== gearId),
    };

    setShowCustomizeTrip(updatedTrip);
  };

  const handleSaveCustomization = async () => {
    if (!showCustomizeTrip) return;

    const success = await updateTrip(() => 
      apiClient.updateTrip(showCustomizeTrip.tripId, {
        gearItems: showCustomizeTrip.gearItems,
      })
    );

    if (success) {
      setShowCustomizeTrip(null);
      refetch();
    }
  };

  // Utility functions
  const getCategoryInfo = (categoryValue: string) => {
    return CATEGORIES.find(c => c.value === categoryValue) || 
           { value: categoryValue, label: categoryValue, icon: '📦' };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return '#3b82f6';
      case 'active': return '#10b981';
      case 'completed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="trips-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your trips...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trips-page">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Failed to load trips</h3>
          <p>{error}</p>
          <button className="primary-button" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="trips-page">
      <div className="page-header">
        <div>
          <h1>My Trips</h1>
          <p>Track your current and upcoming adventures</p>
        </div>
        <button className="primary-button" onClick={handleOpenCreateForm}>
          Plan New Trip
        </button>
      </div>

      <div className="trips-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search trips..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="filter-select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="">All Trips</option>
          <option value="upcoming">Upcoming</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      <div className="trips-grid">
        {filteredTrips.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🗺️</div>
            {trips?.length === 0 ? (
              <>
                <h3>No trips planned yet</h3>
                <p>Start planning your next adventure by creating your first trip from a template.</p>
                <button className="primary-button" onClick={handleOpenCreateForm}>
                  Plan Your First Trip
                </button>
              </>
            ) : (
              <>
                <h3>No matching trips found</h3>
                <p>Try adjusting your search or filter criteria.</p>
                <button className="secondary-button" onClick={() => {
                  setSearchTerm('');
                  setSelectedStatus('');
                }}>
                  Clear Filters
                </button>
              </>
            )}
          </div>
        ) : (
          filteredTrips.map(trip => {
            const status = getTripStatus(trip);
            const progress = calculateProgress(trip);
            
            return (
              <div key={trip.tripId} className={`trip-card trip-${status}`}>
                <div className="trip-card-header">
                  <h3 className="trip-name">{trip.name}</h3>
                  <div className="trip-actions">
                    <button
                      className="action-button view-button"
                      onClick={() => setShowTripDetail(trip)}
                      title="View trip details"
                    >
                      👁️
                    </button>
                    <button
                      className="action-button customize-button"
                      onClick={() => handleOpenCustomizeTrip(trip)}
                      title="Customize gear list"
                    >
                      ⚙️
                    </button>
                    <button
                      className="action-button delete-button"
                      onClick={() => setShowDeleteConfirm(trip.tripId)}
                      title="Delete trip"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="trip-details">
                  <div className="trip-dates">
                    <span className="detail-label">📅</span>
                    <span className="detail-value">
                      {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                    </span>
                  </div>
                  
                  <div className="trip-status">
                    <span 
                      className={`status-badge status-${status}`}
                      style={{ backgroundColor: getStatusColor(status) }}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="trip-progress">
                  <div className="progress-header">
                    <span className="progress-label">Packing Progress</span>
                    <span className="progress-percentage">{progress.percentage}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${progress.percentage}%` }}
                    ></div>
                  </div>
                  <div className="progress-stats">
                    <span className="packed-count">
                      {progress.packedItems} of {progress.totalItems} items packed
                    </span>
                  </div>
                </div>

                {progress.categoriesProgress.length > 0 && (
                  <div className="category-progress">
                    {progress.categoriesProgress.slice(0, 3).map(cat => {
                      const categoryInfo = getCategoryInfo(cat.category);
                      return (
                        <div key={cat.category} className="category-progress-item">
                          <span className="category-icon">{categoryInfo.icon}</span>
                          <span className="category-name">{categoryInfo.label}</span>
                          <span className="category-stats">{cat.packed}/{cat.total}</span>
                        </div>
                      );
                    })}
                    {progress.categoriesProgress.length > 3 && (
                      <div className="category-progress-more">
                        +{progress.categoriesProgress.length - 3} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>      {/*
 Create Trip Form Modal */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={handleCloseCreateForm}>
          <div className="modal-content trip-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Plan New Trip</h2>
              <button className="close-button" onClick={handleCloseCreateForm}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="trip-form">
              <div className="form-section">
                <h3>Trip Details</h3>
                
                <div className="form-group">
                  <label htmlFor="name">Trip Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={formErrors.name ? 'error' : ''}
                    placeholder="Enter trip name"
                  />
                  {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="startDate">Start Date *</label>
                    <input
                      type="date"
                      id="startDate"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className={formErrors.startDate ? 'error' : ''}
                    />
                    {formErrors.startDate && <span className="error-message">{formErrors.startDate}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="endDate">End Date *</label>
                    <input
                      type="date"
                      id="endDate"
                      value={formData.endDate}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                      className={formErrors.endDate ? 'error' : ''}
                    />
                    {formErrors.endDate && <span className="error-message">{formErrors.endDate}</span>}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Select Template</h3>
                
                {templatesLoading ? (
                  <div className="templates-loading">
                    <div className="loading-spinner small"></div>
                    <span>Loading templates...</span>
                  </div>
                ) : !templates || templates.length === 0 ? (
                  <div className="no-templates-available">
                    <p>No templates available. Create a template first to plan trips.</p>
                  </div>
                ) : (
                  <div className="template-selection">
                    {templates.map(template => (
                      <div 
                        key={template.templateId} 
                        className={`template-option ${formData.templateId === template.templateId ? 'selected' : ''}`}
                        onClick={() => handleTemplateSelect(template.templateId)}
                      >
                        <div className="template-option-header">
                          <h4>{template.name}</h4>
                        </div>
                        <div className="template-option-details">
                          <span className="gear-count">{template.gearItems.length} items</span>
                          <div className="category-preview">
                            {Array.from(new Set(template.gearItems.map(item => item.category)))
                              .slice(0, 3)
                              .map(category => {
                                const categoryInfo = getCategoryInfo(category);
                                return (
                                  <span key={category} className="category-icon" title={categoryInfo.label}>
                                    {categoryInfo.icon}
                                  </span>
                                );
                              })}
                            {Array.from(new Set(template.gearItems.map(item => item.category))).length > 3 && (
                              <span className="category-more">+{Array.from(new Set(template.gearItems.map(item => item.category))).length - 3}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {formErrors.templateId && <span className="error-message">{formErrors.templateId}</span>}
              </div>

              {formData.gearItems.length > 0 && (
                <div className="form-section">
                  <h3>Gear Preview ({formData.gearItems.length} items)</h3>
                  <div className="gear-preview-grid">
                    {Array.from(new Set(formData.gearItems.map(item => item.category)))
                      .map(category => {
                        const categoryInfo = getCategoryInfo(category);
                        const categoryItems = formData.gearItems.filter(item => item.category === category);
                        return (
                          <div key={category} className="category-preview-section">
                            <div className="category-preview-header">
                              <span className="category-icon">{categoryInfo.icon}</span>
                              <span className="category-name">{categoryInfo.label}</span>
                              <span className="category-count">({categoryItems.length})</span>
                            </div>
                            <ul className="category-items-preview">
                              {categoryItems.slice(0, 3).map(item => (
                                <li key={item.gearId}>{item.name}</li>
                              ))}
                              {categoryItems.length > 3 && (
                                <li className="more-items">+{categoryItems.length - 3} more</li>
                              )}
                            </ul>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="secondary-button" onClick={handleCloseCreateForm}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={creating}
                >
                  {creating ? 'Creating Trip...' : 'Create Trip'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Trip Detail Modal */}
      {showTripDetail && (
        <div className="modal-overlay" onClick={() => setShowTripDetail(null)}>
          <div className="modal-content trip-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showTripDetail.name}</h2>
              <button className="close-button" onClick={() => setShowTripDetail(null)}>×</button>
            </div>
            
            <div className="trip-detail-content">
              <div className="trip-detail-header">
                <div className="trip-info">
                  <div className="trip-dates">
                    <span className="detail-label">📅 Dates:</span>
                    <span className="detail-value">
                      {formatDate(showTripDetail.startDate)} - {formatDate(showTripDetail.endDate)}
                    </span>
                  </div>
                  <div className="trip-status">
                    <span className="detail-label">Status:</span>
                    <span 
                      className={`status-badge status-${getTripStatus(showTripDetail)}`}
                      style={{ backgroundColor: getStatusColor(getTripStatus(showTripDetail)) }}
                    >
                      {getTripStatus(showTripDetail).charAt(0).toUpperCase() + getTripStatus(showTripDetail).slice(1)}
                    </span>
                  </div>
                </div>

                <div className="trip-progress-detail">
                  {(() => {
                    const progress = calculateProgress(showTripDetail);
                    return (
                      <>
                        <div className="progress-header">
                          <span className="progress-label">Overall Progress</span>
                          <span className="progress-percentage">{progress.percentage}%</span>
                        </div>
                        <div className="progress-bar large">
                          <div 
                            className="progress-fill"
                            style={{ width: `${progress.percentage}%` }}
                          ></div>
                        </div>
                        <div className="progress-stats">
                          <span className="packed-count">
                            {progress.packedItems} of {progress.totalItems} items packed
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="packing-checklist">
                <h3>Packing Checklist</h3>
                
                {showTripDetail.gearItems.length === 0 ? (
                  <div className="no-gear-items">
                    <p>No gear items in this trip.</p>
                  </div>
                ) : (
                  <div className="checklist-by-category">
                    {Array.from(new Set(showTripDetail.gearItems.map(item => item.category)))
                      .sort()
                      .map(category => {
                        const categoryInfo = getCategoryInfo(category);
                        const categoryItems = showTripDetail.gearItems.filter(item => item.category === category);
                        const packedCount = categoryItems.filter(item => item.packed).length;
                        const categoryProgress = Math.round((packedCount / categoryItems.length) * 100);
                        
                        return (
                          <div key={category} className="category-checklist-section">
                            <div className="category-checklist-header">
                              <div className="category-info">
                                <span className="category-icon">{categoryInfo.icon}</span>
                                <h4 className="category-name">{categoryInfo.label}</h4>
                                <span className="category-progress">
                                  ({packedCount}/{categoryItems.length} - {categoryProgress}%)
                                </span>
                              </div>
                              <div className="category-progress-bar">
                                <div 
                                  className="category-progress-fill"
                                  style={{ width: `${categoryProgress}%` }}
                                ></div>
                              </div>
                            </div>
                            
                            <div className="category-items-checklist">
                              {categoryItems
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(item => (
                                  <div key={item.gearId} className={`checklist-item ${item.packed ? 'packed' : ''}`}>
                                    <label className="checklist-label">
                                      <input
                                        type="checkbox"
                                        checked={item.packed}
                                        onChange={(e) => handleToggleItemStatus(
                                          showTripDetail.tripId, 
                                          item.gearId, 
                                          e.target.checked
                                        )}
                                        disabled={updatingStatus}
                                      />
                                      <span className="checkmark"></span>
                                      <span className="item-name">{item.name}</span>
                                    </label>
                                    {item.packed && item.packedAt && (
                                      <span className="packed-time">
                                        Packed {new Date(item.packedAt).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            <div className="trip-detail-actions">
              <button 
                className="secondary-button" 
                onClick={() => setShowTripDetail(null)}
              >
                Close
              </button>
              <button 
                className="primary-button" 
                onClick={() => {
                  setShowTripDetail(null);
                  handleOpenCustomizeTrip(showTripDetail);
                }}
              >
                Customize Gear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trip Customization Modal */}
      {showCustomizeTrip && (
        <div className="modal-overlay" onClick={() => setShowCustomizeTrip(null)}>
          <div className="modal-content trip-customize-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Customize Trip Gear</h2>
              <button className="close-button" onClick={() => setShowCustomizeTrip(null)}>×</button>
            </div>
            
            <div className="customize-content">
              <div className="customize-section">
                <h3>Current Gear ({showCustomizeTrip.gearItems.length} items)</h3>
                
                {showCustomizeTrip.gearItems.length === 0 ? (
                  <div className="no-gear-selected">
                    <p>No gear items in this trip. Add items from your inventory below.</p>
                  </div>
                ) : (
                  <div className="current-gear-list">
                    {showCustomizeTrip.gearItems.map(item => {
                      const categoryInfo = getCategoryInfo(item.category);
                      return (
                        <div key={item.gearId} className={`current-gear-item ${item.packed ? 'packed' : ''}`}>
                          <div className="gear-info">
                            <span className="category-icon">{categoryInfo.icon}</span>
                            <span className="gear-name">{item.name}</span>
                            <span className={`category-badge category-${item.category}`}>
                              {categoryInfo.label}
                            </span>
                            {item.packed && <span className="packed-indicator">✓ Packed</span>}
                          </div>
                          <button
                            type="button"
                            className="remove-gear-button"
                            onClick={() => handleRemoveGearFromTrip(item.gearId)}
                            title="Remove from trip"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="customize-section">
                <h3>Add Gear from Inventory</h3>
                
                {gearLoading ? (
                  <div className="gear-loading">
                    <div className="loading-spinner small"></div>
                    <span>Loading gear inventory...</span>
                  </div>
                ) : !gearItems || gearItems.length === 0 ? (
                  <div className="no-gear-available">
                    <p>No gear items in your inventory. Add gear items first to include them in trips.</p>
                  </div>
                ) : (
                  <>
                    <div className="gear-filters">
                      <div className="search-box">
                        <input
                          type="text"
                          placeholder="Search gear..."
                          className="search-input small"
                          value={gearSearchTerm}
                          onChange={(e) => setGearSearchTerm(e.target.value)}
                        />
                      </div>
                      <select 
                        className="filter-select small"
                        value={selectedGearCategory}
                        onChange={(e) => setSelectedGearCategory(e.target.value)}
                      >
                        <option value="">All Categories</option>
                        {CATEGORIES.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="available-gear-list">
                      {filteredGearItems.length === 0 ? (
                        <div className="no-gear-found">
                          <p>No available gear items match your search criteria.</p>
                        </div>
                      ) : (
                        filteredGearItems.map(gear => {
                          const categoryInfo = getCategoryInfo(gear.category);
                          return (
                            <div key={gear.gearId} className="available-gear-item">
                              <div className="gear-info">
                                <span className="category-icon">{categoryInfo.icon}</span>
                                <span className="gear-name">{gear.name}</span>
                                <span className={`category-badge category-${gear.category}`}>
                                  {categoryInfo.label}
                                </span>
                                {gear.weight && (
                                  <span className="gear-weight">{gear.weight}g</span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="add-gear-button"
                                onClick={() => handleAddGearToTrip(gear)}
                                title="Add to trip"
                              >
                                +
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="customize-actions">
              <button 
                type="button" 
                className="secondary-button" 
                onClick={() => setShowCustomizeTrip(null)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="primary-button"
                onClick={handleSaveCustomization}
                disabled={updating}
              >
                {updating ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Trip</h2>
              <button className="close-button" onClick={() => setShowDeleteConfirm(null)}>×</button>
            </div>
            
            <div className="delete-content">
              <p>Are you sure you want to delete this trip? This action cannot be undone.</p>
              <p className="trip-name-confirm">
                <strong>{trips?.find(t => t.tripId === showDeleteConfirm)?.name}</strong>
              </p>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="secondary-button" 
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="danger-button"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Trip'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trips;